/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Checkbox,
    Stack,

    /* Hooks */
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    ADD_FILTER,
    REMOVE_FILTER
} from '@salesforce/retail-react-app/app/pages/product-list/partials/refinements-utils'

const CheckboxRefinements = ({filter, toggleFilter, selectedFilters}) => {
    const refinementsStyles = useMultiStyleConfig('Refinements')
    const {formatMessage} = useIntl()

    // Find all available filter values (those with hitCount > 0 or selected)
    const availableValues = filter.values || []
    const availableValueIds = new Set(availableValues.map((v) => v.value))

    // Find selected filters that are not in the available values list
    const unavailableSelectedFilters = selectedFilters.filter(
        (selectedValue) => !availableValueIds.has(selectedValue)
    )

    // Create dummy filter objects for unavailable selected filters
    const dummyFilters = unavailableSelectedFilters.map((value) => {
        // Try to find the original filter data from all filter values
        const originalValue = filter.values?.find((v) => v.value === value)
        return (
            originalValue || {
                value: value,
                label: value,
                hitCount: 0
            }
        )
    })

    // Combine dummy filters with available filters
    const allFilters = [...dummyFilters, ...availableValues]

    return (
        <Stack spacing={4}>
            {allFilters.map((value) => {
                const isChecked = selectedFilters.includes(value.value)
                // Don't display refinements with no results, unless we got there by selecting too
                // many refinements
                if (value.hitCount === 0 && !isChecked) return null

                return (
                    <Box key={value.value} {...refinementsStyles.checkboxContainer}>
                        <Checkbox
                            variant="filters"
                            isChecked={isChecked}
                            onChange={() => toggleFilter(value, filter.attributeId, isChecked)}
                            aria-label={formatMessage(
                                isChecked ? REMOVE_FILTER : ADD_FILTER,
                                value
                            )}
                        >
                            {value.label}
                        </Checkbox>
                    </Box>
                )
            })}
        </Stack>
    )
}

CheckboxRefinements.propTypes = {
    filter: PropTypes.object,
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.array
}

export default CheckboxRefinements
